import { useState, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Loader2, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api, CustomFieldDefinition, CustomFieldInput } from '@/lib/api';

export function ManageCustomFieldsDialog({
  open,
  onOpenChange,
  fields,
  onFieldsChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: CustomFieldDefinition[];
  onFieldsChanged: () => void;
}) {
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<CustomFieldInput['fieldType']>('text');
  const [newSelectOptions, setNewSelectOptions] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    if (!newFieldName.trim()) return;
    setCreating(true);
    try {
      const input: CustomFieldInput = {
        fieldName: newFieldName.trim(),
        fieldType: newFieldType,
      };
      if (newFieldType === 'select' && newSelectOptions.trim()) {
        input.selectOptions = newSelectOptions
          .split(',')
          .map((o) => o.trim())
          .filter(Boolean);
      }
      await api.createCustomField(input);
      setNewFieldName('');
      setNewSelectOptions('');
      onFieldsChanged();
      toast({ description: 'Custom field created.' });
    } catch (err) {
      toast({
        title: 'Failed to create field',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [newFieldName, newFieldType, newSelectOptions, onFieldsChanged]);

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await api.deleteCustomField(id);
        onFieldsChanged();
        toast({ description: 'Custom field deleted.' });
      } catch (err) {
        toast({
          title: 'Failed to delete field',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      } finally {
        setDeletingId(null);
      }
    },
    [onFieldsChanged],
  );

  const TYPE_LABELS: Record<string, string> = {
    text: 'Text',
    number: 'Number',
    date: 'Date',
    url: 'URL',
    select: 'Select',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Custom Fields</DialogTitle>
          <DialogDescription>
            Define custom fields that appear on all your contacts.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            {/* Existing fields */}
            {fields.length > 0 && (
              <div className="space-y-2">
                {fields.map((field) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 p-2 rounded border bg-muted/30"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{field.fieldName}</p>
                      <p className="text-xs text-muted-foreground">
                        {TYPE_LABELS[field.fieldType] || field.fieldType}
                        {field.selectOptions?.length ? ` (${field.selectOptions.join(', ')})` : ''}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => handleDelete(field.id)}
                      disabled={deletingId === field.id}
                    >
                      {deletingId === field.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {fields.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No custom fields defined yet.
              </p>
            )}

            {/* Add new field */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-sm font-medium">Add New Field</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Field name"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="flex-1"
                />
                <Select
                  value={newFieldType}
                  onValueChange={(v) => setNewFieldType(v as CustomFieldInput['fieldType'])}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="url">URL</SelectItem>
                    <SelectItem value="select">Select</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newFieldType === 'select' && (
                <Input
                  placeholder="Options (comma-separated)"
                  value={newSelectOptions}
                  onChange={(e) => setNewSelectOptions(e.target.value)}
                />
              )}
              <Button size="sm" onClick={handleCreate} disabled={creating || !newFieldName.trim()}>
                {creating ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Add Field
              </Button>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders custom field inputs for use inside ContactFormDialog.
 */
export function CustomFieldInputs({
  fields,
  values,
  onChange,
  onManageFields,
}: {
  fields: CustomFieldDefinition[];
  values: Record<string, unknown>;
  onChange: (fieldName: string, value: unknown) => void;
  onManageFields: () => void;
}) {
  if (fields.length === 0) {
    return (
      <div className="flex items-center justify-between py-2">
        <p className="text-sm text-muted-foreground">No custom fields defined.</p>
        <Button variant="outline" size="sm" onClick={onManageFields}>
          <Settings2 className="h-3.5 w-3.5 mr-1" />
          Manage Fields
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Custom Fields</Label>
        <Button variant="ghost" size="sm" onClick={onManageFields} className="h-7 text-xs">
          <Settings2 className="h-3 w-3 mr-1" />
          Manage
        </Button>
      </div>
      {fields.map((field) => (
        <div key={field.id} className="space-y-1">
          <Label className="text-xs text-muted-foreground">{field.fieldName}</Label>
          {field.fieldType === 'select' ? (
            <Select
              value={(values[field.fieldName] as string) || ''}
              onValueChange={(v) => onChange(field.fieldName, v || null)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {(field.selectOptions || []).map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={
                field.fieldType === 'number'
                  ? 'number'
                  : field.fieldType === 'date'
                    ? 'date'
                    : field.fieldType === 'url'
                      ? 'url'
                      : 'text'
              }
              value={(values[field.fieldName] as string) ?? ''}
              onChange={(e) => {
                const val =
                  field.fieldType === 'number'
                    ? e.target.value
                      ? Number(e.target.value)
                      : null
                    : e.target.value || null;
                onChange(field.fieldName, val);
              }}
              placeholder={field.fieldName}
              className="h-8 text-sm"
            />
          )}
        </div>
      ))}
    </div>
  );
}
